/**
 * NIGHTBOUND - procedural creature placeholders.
 *
 * These are hand-authored SVG silhouettes drawn in code. They are only ever
 * used when no artwork file can be found on disk, which means the game stays
 * readable with an empty `public/assets/creatures` folder and previews the
 * correct species before any art has been dropped in.
 *
 * They are deliberately *not* the shipped art: the shipped art lives as real
 * files under `public/assets/creatures/<species>/`.
 */

import type { CreatureStateId } from '../../shared/creatureState'
import type { SpeciesId } from '../../shared/species'

interface ProceduralCreatureProps {
  speciesId: SpeciesId
  accent: string
  state: CreatureStateId
  className?: string
}

/** Lighten (amount > 0) or darken (amount < 0) a hex colour. */
function shade(hex: string, amount: number): string {
  const value = hex.replace('#', '')
  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)
  const target = amount >= 0 ? 236 : 6
  const t = Math.min(1, Math.abs(amount))
  const mix = (channel: number) => Math.round(channel + (target - channel) * t)
  return `#${[mix(r), mix(g), mix(b)]
    .map((c) => Math.max(0, Math.min(255, c)).toString(16).padStart(2, '0'))
    .join('')}`
}

function Eyes({
  state,
  cx1,
  cx2,
  cy,
  r = 4.6,
}: {
  state: CreatureStateId
  cx1: number
  cx2: number
  cy: number
  r?: number
}) {
  if (state === 'SLEEPING') {
    return (
      <g stroke="#050506" strokeWidth={2.4} strokeLinecap="round" fill="none">
        <path d={`M${cx1 - r} ${cy} q${r} ${r} ${r * 2} 0`} />
        <path d={`M${cx2 - r} ${cy} q${r} ${r} ${r * 2} 0`} />
      </g>
    )
  }
  if (state === 'INJURED' || state === 'HUNGRY') {
    return (
      <g stroke="#050506" strokeWidth={2.3} strokeLinecap="round" fill="none">
        <path d={`M${cx1 - r} ${cy} L${cx1 + r} ${cy}`} />
        <path d={`M${cx2 - r} ${cy} L${cx2 + r} ${cy}`} />
      </g>
    )
  }
  return (
    <g>
      <circle cx={cx1} cy={cy} r={r} fill="#050506" />
      <circle cx={cx2} cy={cy} r={r} fill="#050506" />
      <circle cx={cx1 + r * 0.4} cy={cy - r * 0.45} r={r * 0.32} fill="#ece6da" opacity={0.9} />
      <circle cx={cx2 + r * 0.4} cy={cy - r * 0.45} r={r * 0.32} fill="#ece6da" opacity={0.9} />
    </g>
  )
}

function Dog({ accent, state }: { accent: string; state: CreatureStateId }) {
  const body = shade(accent, -0.08)
  const belly = shade(accent, -0.3)
  return (
    <g>
      <path d="M76 150 q-28 -8 -32 -34" fill="none" stroke={body} strokeWidth={13} strokeLinecap="round" />
      <rect x={92} y={156} width={15} height={42} rx={7} fill={belly} />
      <rect x={150} y={156} width={15} height={42} rx={7} fill={belly} />
      <ellipse cx={124} cy={146} rx={54} ry={32} fill={body} />
      <ellipse cx={118} cy={158} rx={40} ry={19} fill={belly} opacity={0.5} />
      <path d="M158 96 l-8 -32 l28 16 z" fill={belly} />
      <path d="M192 96 l10 -30 l-24 14 z" fill={belly} />
      <circle cx={174} cy={116} r={26} fill={body} />
      <ellipse cx={198} cy={128} rx={17} ry={11} fill={belly} />
      <circle cx={210} cy={125} r={4} fill="#050506" />
      <Eyes state={state} cx1={168} cx2={184} cy={110} />
    </g>
  )
}

function Cat({ accent, state }: { accent: string; state: CreatureStateId }) {
  const body = shade(accent, -0.06)
  const belly = shade(accent, -0.22)
  return (
    <g>
      <path
        d="M162 192 q36 -6 32 -48 q-3 -20 -22 -22"
        fill="none"
        stroke={body}
        strokeWidth={11}
        strokeLinecap="round"
      />
      <path d="M92 202 q-12 -62 34 -72 q46 -8 48 42 q2 26 -8 32 z" fill={body} />
      <ellipse cx={128} cy={178} rx={25} ry={25} fill={belly} opacity={0.45} />
      <path d="M104 86 l-12 -32 l32 16 z" fill={body} />
      <path d="M148 86 l14 -32 l-32 16 z" fill={body} />
      <path d="M109 83 l-7 -18 l19 10 z" fill={belly} opacity={0.7} />
      <path d="M143 83 l8 -18 l-19 10 z" fill={belly} opacity={0.7} />
      <circle cx={126} cy={106} r={30} fill={body} />
      <path d="M119 122 q7 7 14 0" stroke={belly} strokeWidth={2.2} fill="none" strokeLinecap="round" />
      <Eyes state={state} cx1={115} cx2={137} cy={102} />
    </g>
  )
}

function Bird({ accent, state }: { accent: string; state: CreatureStateId }) {
  const body = shade(accent, -0.05)
  const wing = shade(accent, -0.3)
  return (
    <g>
      <path d="M94 152 l-48 24 l42 6 z" fill={wing} />
      <path d="M120 186 l-2 18 M138 186 l2 18" stroke="#3d3d3f" strokeWidth={3} strokeLinecap="round" />
      <path d="M104 202 l14 0 M124 202 l14 0" stroke="#3d3d3f" strokeWidth={2.4} strokeLinecap="round" />
      <ellipse cx={124} cy={150} rx={38} ry={44} fill={body} />
      <ellipse cx={113} cy={154} rx={19} ry={30} fill={wing} opacity={0.85} />
      <circle cx={150} cy={104} r={24} fill={body} />
      <path d="M170 104 l24 7 l-24 8 z" fill="#c9a46a" />
      <Eyes state={state} cx1={150} cx2={160} cy={99} r={3.6} />
    </g>
  )
}

function Snake({ accent, state }: { accent: string; state: CreatureStateId }) {
  const body = shade(accent, -0.04)
  const belly = shade(accent, -0.34)
  return (
    <g>
      <path
        d="M100 200 q-20 -22 6 -34 q28 -12 50 3 q22 15 4 31 q-18 15 -44 8 q-22 -7 -10 -22 q9 -12 31 -9"
        fill="none"
        stroke={body}
        strokeWidth={21}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M100 200 q-20 -22 6 -34 q28 -12 50 3 q22 15 4 31"
        fill="none"
        stroke={belly}
        strokeWidth={5}
        strokeLinecap="round"
        opacity={0.5}
      />
      <ellipse cx={94} cy={118} rx={23} ry={16} fill={body} transform="rotate(-24 94 118)" />
      <Eyes state={state} cx1={88} cx2={101} cy={114} r={3.5} />
      {state !== 'SLEEPING' ? (
        <path
          d="M76 122 l-18 2 m0 0 l-7 -5 m7 5 l-7 5"
          stroke="#a8452f"
          strokeWidth={2.4}
          fill="none"
          strokeLinecap="round"
        />
      ) : null}
    </g>
  )
}

export function ProceduralCreature({
  speciesId,
  accent,
  state,
  className,
}: ProceduralCreatureProps) {
  return (
    <svg
      viewBox="0 0 240 240"
      className={className}
      preserveAspectRatio="xMidYMax meet"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse cx={124} cy={206} rx={78} ry={12} fill="#000000" opacity={0.45} />
      {speciesId === 'DOG' ? <Dog accent={accent} state={state} /> : null}
      {speciesId === 'CAT' ? <Cat accent={accent} state={state} /> : null}
      {speciesId === 'BIRD' ? <Bird accent={accent} state={state} /> : null}
      {speciesId === 'SNAKE' ? <Snake accent={accent} state={state} /> : null}
    </svg>
  )
}
